import React from 'react';
import { TimeSlot } from './types';

interface EventDisplayProps {
  timeSlot: TimeSlot | null;
  dayName: string;
}

const EventDisplay: React.FC<EventDisplayProps> = ({ timeSlot, dayName }) => {
  // Get color variables from CSS
  const getColorVar = (varName: string, fallback: string) => {
    return `var(${varName}, ${fallback})`;
  };

  // Get RGB values for shadows
  const getRGBFromVar = (varName: string, fallback: string) => {
    const value = getComputedStyle(document.documentElement)
      .getPropertyValue(varName)
      .trim();
    return value || fallback;
  };

  const primaryRGB = getRGBFromVar('--color-primary-rgb', '220, 38, 38');
  const accentRGB = getRGBFromVar('--color-accent-rgb', '139, 92, 246');
  const secondaryRGB = getRGBFromVar('--color-secondary-rgb', '59, 130, 246');

  if (!timeSlot) {
    return (
      <div
        style={{
          padding: '24px',
          textAlign: 'center',
          color: getColorVar('--color-text-muted', '#a8a29e'),
          fontFamily: 'Rajdhani, sans-serif',
          fontSize: '18px',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <p style={{ 
          marginBottom: '8px', 
          fontFamily: 'Orbitron, sans-serif', 
          color: getColorVar('--color-primary', '#dc2626'),
          fontSize: '20px',
          fontWeight: 600,
        }}>
          {dayName}
        </p>
        <p style={{ opacity: 0.7 }}>Select a time to view events</p>
      </div>
    );
  }

  return (
    <div
      style={{
        padding: '20px',
        animation: 'fadeIn 0.3s ease-out',
        height: '100%',
      }}
    >
      <style>
        {`
          @keyframes fadeIn {
            from { opacity: 0; transform: translateY(-10px); }
            to { opacity: 1; transform: translateY(0); }
          }
        `}
      </style>
      
      {/* Time Header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '16px',
          marginBottom: '20px',
          paddingBottom: '16px',
          borderBottom: `1px solid ${getColorVar('--color-border', '#374151')}`,
        }}
      >
        <div
          style={{
            fontFamily: 'Orbitron, sans-serif',
            fontSize: '28px',
            fontWeight: 700,
            color: getColorVar('--color-primary', '#dc2626'),
            textShadow: `0 0 20px rgba(${primaryRGB}, 0.5)`,
          }}
        >
          {timeSlot.displayTime}
        </div>
        <div
          style={{
            padding: '4px 12px',
            background: `rgba(${primaryRGB}, 0.15)`,
            borderRadius: '20px',
            fontSize: '12px',
            fontWeight: 600,
            color: getColorVar('--color-primary-light', '#ef4444'),
            border: `1px solid rgba(${primaryRGB}, 0.3)`,
          }}
        >
          {dayName.split(' - ')[0]}
        </div>
      </div>

      {/* Events Grid */}
      <div
        style={{
          display: 'grid',
          gap: '16px',
        }}
      >
        {timeSlot.events.map((event, index) => (
          <div
            key={event.id}
            style={{
              padding: '16px',
              background: `linear-gradient(
                135deg, 
                ${getColorVar('--color-surface', '#262626')} 0%, 
                ${getColorVar('--color-background-alt', '#1e1e1e')} 100%
              )`,
              borderRadius: '12px',
              border: `1px solid ${getColorVar('--color-border', '#374151')}`,
              transition: 'all 0.3s ease',
              animation: `fadeIn 0.3s ease-out ${index * 0.1}s both`,
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = getColorVar('--color-primary', '#dc2626');
              e.currentTarget.style.boxShadow = `0 0 20px rgba(${primaryRGB}, 0.2)`;
              e.currentTarget.style.transform = 'translateX(5px)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = getColorVar('--color-border', '#374151');
              e.currentTarget.style.boxShadow = 'none';
              e.currentTarget.style.transform = 'translateX(0)';
            }}
          >
            {/* Event Type Badge */}
            <div
              style={{
                display: 'inline-block',
                padding: '2px 8px',
                marginBottom: '8px',
                background: event.type === 'technical' 
                  ? `rgba(${secondaryRGB}, 0.2)` 
                  : `rgba(${accentRGB}, 0.2)`,
                color: event.type === 'technical' 
                  ? getColorVar('--color-secondary', '#3b82f6')
                  : getColorVar('--color-accent', '#8b5cf6'),
                borderRadius: '4px',
                fontSize: '10px',
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '1px',
              }}
            >
              {event.type}
            </div>
            
            {/* Event Name */}
            <h3
              style={{
                fontFamily: 'Orbitron, sans-serif',
                fontSize: '16px',
                fontWeight: 600,
                color: getColorVar('--color-text', '#f3f4f6'),
                marginBottom: '6px',
              }}
            >
              {event.name}
            </h3>
            
            {/* Event Description */}
            <p
              style={{
                fontSize: '14px',
                color: getColorVar('--color-text-muted', '#a8a29e'),
                marginBottom: '10px',
                lineHeight: 1.4,
              }}
            >
              {event.description}
            </p>
            
            {/* Venue */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                fontSize: '13px',
                color: getColorVar('--color-primary', '#dc2626'),
              }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" 
                stroke={getColorVar('--color-primary', '#dc2626')} strokeWidth="2">
                <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
                <circle cx="12" cy="10" r="3"/>
              </svg>
              <span>{event.venue}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default EventDisplay;